/// Active runway selection — shared between the spawner (takeoff
/// runway), the approach-course renderer (which extended-centerline
/// to draw), and the cleared-to-land flow (which runway end to
/// validate against).
///
/// "Active" is decided by the level's `fixedWindDirection`: aircraft
/// land into the wind, so the runway end whose heading is closest to
/// the wind direction is the one in use. With no wind set, fall back
/// to the airport's first runway's heading1 — deterministic, and the
/// caller can override later when we add a wind sim.

import type { Airport, Runway } from '../types/LevelData'

export interface RunwayPick {
  airport: Airport
  runway: Runway
  /// The magnetic heading the aircraft must be flying TO LAND on this
  /// end (e.g. 220 for runway 22).
  headingDeg: number
}

/// Pick the active landing end of a single airport given wind.
export function activeRunwayEnd(
  airport: Airport,
  fixedWindDirection: number | null | undefined,
): RunwayPick | null {
  if (airport.runways.length === 0) return null

  if (fixedWindDirection == null) {
    const r = airport.runways[0]
    if (!r) return null
    return { airport, runway: r, headingDeg: r.heading1 }
  }

  let best: { pick: RunwayPick; delta: number } | null = null
  for (const runway of airport.runways) {
    for (const h of [runway.heading1, runway.heading2]) {
      const delta = Math.abs((((fixedWindDirection - h + 540) % 360) - 180))
      if (!best || delta < best.delta) {
        best = { pick: { airport, runway, headingDeg: h }, delta }
      }
    }
  }
  return best?.pick ?? null
}

/// Pick the active landing end at the level — preferring the
/// aircraft's goal airport (if a `land` goal), else the named focus
/// airport, else the first airport. Used by the cleared-to-land
/// auto-pick in the absence of an in-game runway-end selector UI.
export function activeRunwayForLanding(args: {
  airports: Airport[]
  goalAirportName?: string
  focusAirportName?: string
  fixedWindDirection: number | null | undefined
}): RunwayPick | null {
  const { airports, goalAirportName, focusAirportName, fixedWindDirection } = args
  const target =
    (goalAirportName && airports.find((a) => a.name === goalAirportName)) ||
    (focusAirportName && airports.find((a) => a.name === focusAirportName)) ||
    airports[0]
  if (!target) return null
  return activeRunwayEnd(target, fixedWindDirection)
}
